import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  X, Trash2, FileText, Activity, ExternalLink, Save, RefreshCcw,
  AlertTriangle, Wifi, Eye, EyeOff, Mail, Ticket, TicketX, Loader2,
  Copy, CheckCircle2, ArrowRight, Upload, Users,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import SyncroLinkerModal from "@/components/SyncroLinkerModal";
import SyncroMergeModal from "@/components/SyncroMergeModal";
import { CLIENT_FIELDS, PORT_FIELDS, inputCls, fmtDate, fmtMoney } from "./utils";

// The drawer is the busiest surface in the app — every review /
// activate / port / handover / mismatch-resolution flow lives here.
// Sub-panels stay in-file so they can share ``draft`` state via
// props without prop-drilling through a fresh module boundary. When
// this file grows past ~1600 lines, consider splitting sub-panels
// into ``components/kanban/drawer/*``.

export function ClientDrawer({ client, board, isPartnerView, onClose, onChanged }) {
  const [draft, setDraft] = useState(client);
  const [activity, setActivity] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [showSyncroLinker, setShowSyncroLinker] = useState(false);
  const [showSyncroMerge, setShowSyncroMerge] = useState(false);
  useEffect(() => { setDraft(client); }, [client.id]);

  const baseUrl = isPartnerView ? "/partner/migration" : "/commissions/migrations";
  const clientUrl = isPartnerView ? `/partner/migration/clients/${client.id}` : `/commissions/migrations/clients/${client.id}`;

  async function loadActivity() {
    try {
      const { data } = await api.get(`${clientUrl}/activity`);
      setActivity(data.activity || []);
    } catch (_) { /* activity load is best-effort */ }
  }
  useEffect(() => { loadActivity(); }, [client.id, clientUrl]);

  async function save() {
    setBusy(true);
    try {
      const { data } = await api.patch(clientUrl, draft);
      toast.success("Saved");
      onChanged(data);
      loadActivity();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  }

  async function del() {
    if (!window.confirm(`Delete ${client.business_name || client.email}? This can't be undone.`)) return;
    try {
      await api.delete(`${baseUrl}/clients/${client.id}`);
      toast.success("Deleted");
      onChanged("deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  }

  async function resetForTesting() {
    const label = client.business_name || client.email || "this card";
    const linkedBits = [
      client.syncro_ticket_number ? `Syncro #${client.syncro_ticket_number}` : null,
      client.linked_signup_id ? "wizard signup link" : null,
      client.nbn_password ? "NBN credentials" : null,
      (Number(client.note_count) || 0) > 0 ? `${client.note_count} activity row${client.note_count === 1 ? "" : "s"}` : null,
    ].filter(Boolean);
    const tail = linkedBits.length
      ? `\n\nThis will clear: ${linkedBits.join(", ")}.`
      : "";
    if (!window.confirm(
      `Reset ${label} for testing?${tail}\n\n` +
      "The card stays on the board with its identity + current-plan data, but " +
      "the Syncro tie, signup link, NBN handover creds, plan-mismatch ack, " +
      "and the entire activity feed will be cleared, and the stage moved back to " +
      "Not contacted.\n\n" +
      "Delete the Syncro customer/ticket manually first if you haven't already."
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/commissions/migrations/clients/${client.id}/reset-for-testing`);
      const bits = [];
      if (data.cleared?.syncro_ticket_number) bits.push(`Syncro #${data.cleared.syncro_ticket_number}`);
      if (data.cleared?.linked_signup_id) bits.push("signup");
      if (data.cleared?.had_nbn_creds) bits.push("NBN creds");
      if (data.cleared?.activity_rows_deleted > 0) bits.push(`${data.cleared.activity_rows_deleted} activity row${data.cleared.activity_rows_deleted === 1 ? "" : "s"}`);
      toast.success(`Reset complete${bits.length ? " — cleared " + bits.join(", ") : ""}`);
      onChanged(data.client || "reset");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reset failed");
    } finally { setBusy(false); }
  }

  const [syncing, setSyncing] = useState(false);
  const [dismissedTicketPrompt, setDismissedTicketPrompt] = useState(false);
  useEffect(() => { setDismissedTicketPrompt(false); }, [client.id]);
  async function syncOne({ force = false } = {}) {
    if (isPartnerView) return;
    setSyncing(true);
    try {
      const url = `/commissions/migrations/clients/${client.id}/sync-syncro${force ? "?force=true" : ""}`;
      const { data } = await api.post(url);
      if (data.ok) {
        toast.success(data.reason === "already_synced"
          ? `Already synced to ticket #${data.ticket_number || data.ticket_id}`
          : `Synced to Syncro ticket #${data.ticket_number || data.ticket_id}`);
        const { data: fresh } = await api.get(`${baseUrl}/clients/${client.id}/activity`);
        setActivity(fresh.activity || []);
        try {
          const board = await api.get(baseUrl.startsWith("/partner") ? "/partner/migration" : `/commissions/migrations/${client.partner_code}`);
          const updated = (board.data.clients || []).find(c => c.id === client.id);
          if (updated) onChanged(updated);
        } catch (_) { /* refresh best-effort */ }
      } else {
        toast.error(`Sync failed: ${data.reason || "unknown"}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sync failed");
    } finally { setSyncing(false); }
  }

  // v1.10.99 — manual re-push of the Static IP addon line to the
  // customer's Syncro recurring invoice. Covers retro cases (customer
  // opted in before the auto-flow shipped) and re-pushes when the
  // initial auto-attempt was skipped (no recurring schedule at ticket
  // creation time). Backend handles all failure paths + logs to the
  // activity feed, so we just refresh + toast the outcome.
  const [pushingStaticIp, setPushingStaticIp] = useState(false);
  async function pushStaticIpLine() {
    setPushingStaticIp(true);
    try {
      const { data } = await api.post(
        `/commissions/migrations/clients/${client.id}/push-static-ip-line`);
      if (data.ok) {
        toast.success(`Static IP line added to Syncro schedule #${data.syncro_schedule_id}`);
      } else if (data.reason === "no_recurring_schedule") {
        toast.info("No Syncro recurring invoice set up yet — set one up first, then re-run this push. See the ticket comments for the queued reminder.");
      } else if (data.reason === "no_product_id_configured") {
        toast.error("Static IP SKU not configured — set static_ip_syncro_upc_code in Business settings, wait for the 30-min price-sync to land the product id, then retry.");
      } else {
        toast.error(`Push skipped: ${data.reason || "unknown"}`);
      }
      loadActivity();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Push failed");
    } finally { setPushingStaticIp(false); }
  }

  async function addNote() {
    if (!note.trim()) return;
    try {
      await api.post(`${clientUrl}/notes`, { body: note });
      setNote("");
      loadActivity();
      // Flag the add-note hint so the user clearly sees that "Email
      // partner" is a separate per-row action. Without this, admins
      // frequently added a note and waited for a reply to an email
      // that never left the system.
      toast.success("Note logged — click 'Email partner' on the new row to send it to the partner", {
        duration: 6000,
      });
    } catch (e) {
      toast.error("Note failed");
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 md:p-8" onClick={onClose}>
      <div
        className="w-full max-w-6xl max-h-[92vh] rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl shadow-black/60 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="client-drawer"
      >
        <div className="sticky top-0 bg-slate-900/95 backdrop-blur-md border-b border-slate-800 px-6 py-4 flex items-start justify-between z-10">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-semibold">{board.partner_code}</div>
            <h3 className="font-display text-2xl tracking-tight truncate">{client.business_name || client.email || "Client"}</h3>
            {client.avc_number && (
              <div
                data-testid="drawer-avc-badge"
                className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-cyan-500/30 bg-cyan-500/10"
                title="NBN Access Virtual Circuit — copy me"
              >
                <span className="text-[9px] uppercase tracking-widest text-cyan-400/80 font-semibold">AVC</span>
                <span className="font-mono text-xs text-cyan-100">{client.avc_number}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    try {
                      navigator.clipboard.writeText(client.avc_number);
                      toast.success("AVC copied");
                    } catch { /* clipboard blocked */ }
                  }}
                  className="ml-0.5 -mr-1 text-cyan-400/70 hover:text-cyan-200 p-0.5 rounded"
                  title="Copy AVC number"
                >
                  <Copy className="w-3 h-3" />
                </button>
              </div>
            )}
            {client.static_ip_addon && (
              <span
                data-testid="drawer-static-ip-badge"
                title="Customer opted in for the Static IP addon on signup"
                className="ml-2 mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10"
              >
                <span className="text-[9px] uppercase tracking-widest text-amber-300/90 font-semibold">Static IP</span>
                <span className="text-xs text-amber-100">+addon</span>
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-100 p-1 -m-1 rounded-lg hover:bg-slate-800 transition-colors" title="Close (Esc)"><X className="w-5 h-5"/></button>
        </div>
        {/* Missing-Syncro-ticket prompt */}
        {!isPartnerView
          && !client.syncro_ticket_id
          && client.partner_code
          && !dismissedTicketPrompt && (
          <div
            className="mx-6 mt-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 flex items-start gap-3"
            data-testid="drawer-missing-ticket-prompt"
          >
            <TicketX className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-amber-100 font-semibold">
                This customer isn&apos;t in Syncro yet
              </div>
              <div className="text-xs text-amber-200/80 mt-0.5">
                Would you like me to create a Syncro customer + tracking
                ticket now? It&apos;ll follow the standard{" "}
                <span className="font-mono text-amber-100">
                  NEW SIGNUP — plan — business
                </span>{" "}
                naming.
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setDismissedTicketPrompt(true)}
                data-testid="drawer-ticket-prompt-dismiss"
                className="px-3 py-1.5 text-xs text-amber-200/80 hover:text-amber-100 font-medium"
              >
                Not now
              </button>
              <button
                type="button"
                onClick={() => syncOne({ force: true })}
                disabled={syncing}
                data-testid="drawer-ticket-prompt-create"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-400 hover:bg-amber-300 text-slate-950 text-xs font-semibold disabled:opacity-60"
              >
                {syncing ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Ticket className="w-3.5 h-3.5" />
                )}
                {syncing ? "Creating…" : "Create Syncro ticket"}
              </button>
            </div>
          </div>
        )}
        <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {CLIENT_FIELDS.map(f => (
            <label key={f.key} className={`block ${f.multiline ? "md:col-span-2" : ""}`}>
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">{f.label}</span>
              {f.multiline ? (
                <textarea
                  data-testid={`drawer-field-${f.key}`}
                  rows={3} value={draft[f.key] || ""}
                  onChange={(e) => setDraft({...draft, [f.key]: e.target.value})}
                  className={inputCls}
                />
              ) : (
                <input
                  data-testid={`drawer-field-${f.key}`}
                  type={f.type || "text"} value={draft[f.key] || ""}
                  onChange={(e) => setDraft({...draft, [f.key]: e.target.value})}
                  className={inputCls}
                />
              )}
            </label>
          ))}
          <label className="block md:col-span-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Stage</span>
            <select
              data-testid="drawer-field-stage"
              value={draft.stage || ""}
              onChange={(e) => setDraft({...draft, stage: e.target.value})}
              className={inputCls}
            >
              {board.stages.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
        </div>

        <PortingDetails draft={draft} setDraft={setDraft} />

        <NBNCredentialsPanel
          client={client}
          draft={draft}
          setDraft={setDraft}
          isPartnerView={isPartnerView}
        />

        {/* Static-IP addon push panel — admin-only, only when the customer
            opted in AND has a Syncro customer. Hidden when there's no
            Syncro tie yet (the missing-ticket prompt at the top of the
            drawer covers that path). */}
        {!isPartnerView && client.static_ip_addon && client.syncro_customer_id && (
          <div className="px-5 pb-4" data-testid="drawer-static-ip-push">
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4">
              <div className="flex items-start gap-3">
                <Wifi className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-widest text-amber-300 font-semibold">
                    Static IP recurring invoice line
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    Appends the tenant&apos;s Static IP addon SKU to this
                    customer&apos;s Syncro recurring invoice schedule.
                    Runs automatically at ticket creation; use this to
                    re-push if the initial attempt was skipped (no
                    recurring schedule yet) or to add it retroactively.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={pushStaticIpLine}
                  disabled={pushingStaticIp}
                  data-testid="drawer-static-ip-push-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-semibold disabled:opacity-60"
                >
                  {pushingStaticIp
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <ArrowRight className="w-3.5 h-3.5" />}
                  {pushingStaticIp ? "Pushing…" : "Push static IP line"}
                </button>
              </div>
            </div>
          </div>
        )}

        <ComparePanel client={client} isPartnerView={isPartnerView} onChanged={onChanged} />

        {!isPartnerView && !client.linked_signup_id && (
          <ManualLinkPanel client={client} onLinked={(c) => onChanged(c)} />
        )}

        {!isPartnerView && (
          <ManualSignupPanel client={client} onChanged={onChanged} />
        )}

        {!isPartnerView && client.source === "signup_wizard" && (
          <MergeIntoPanel
            client={client}
            onMerged={(mergedClient) => {
              toast.success(`Merged into ${mergedClient.business_name || "imported card"}`);
              onChanged("deleted");
            }}
          />
        )}

        <div className="px-5 pb-5 flex items-center justify-between border-b border-slate-800">
          {isPartnerView ? (
            <span className="text-xs text-slate-500 italic" data-testid="drawer-delete-disabled">
              Contact Steele Cloud to remove a customer.
            </span>
          ) : (
            <div className="flex items-center gap-4">
              <button
                data-testid="drawer-delete"
                onClick={del}
                className="text-red-400 hover:text-red-300 text-sm inline-flex items-center gap-1.5"
              >
                <Trash2 className="w-4 h-4" /> Delete
              </button>
              <button
                data-testid="drawer-reset-for-testing"
                onClick={resetForTesting}
                disabled={busy}
                className="text-amber-400 hover:text-amber-300 text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
                title="Cut Syncro ties + signup link + NBN creds and move stage back to Not contacted — for restart-a-test flows"
              >
                <RefreshCcw className="w-4 h-4" /> Reset for testing
              </button>
            </div>
          )}
          <button
            data-testid="drawer-save"
            onClick={save} disabled={busy}
            className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60"
          >
            <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save changes"}
          </button>
        </div>

        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 inline-flex items-center gap-2">
              <Activity className="w-3.5 h-3.5" /> Activity
            </h4>
            {!isPartnerView && client.syncro_ticket_id && (
              <button
                data-testid="resync-syncro-btn"
                onClick={async () => {
                  try {
                    const { data } = await api.post(`/commissions/migrations/clients/${client.id}/resync-syncro-comments`);
                    if (data.skipped) {
                      toast.info(`Skipped: ${data.skipped.replace(/_/g, " ")}`);
                    } else {
                      toast.success(`Reconciled ${data.checked || 0} comment${data.checked === 1 ? "" : "s"} — ${data.newly_deleted || 0} newly deleted`);
                      loadActivity();
                    }
                  } catch (e) {
                    toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Resync failed");
                  }
                }}
                title="Poll Syncro for any comments deleted since our last check"
                className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-cyan-300"
              >
                <RefreshCcw className="w-3 h-3" /> Resync from Syncro
              </button>
            )}
            {!isPartnerView && !client.syncro_ticket_id && (
              <button
                data-testid="find-in-syncro-btn"
                onClick={() => setShowSyncroLinker(true)}
                title="Search Syncro by name, pick the install ticket, and auto-extract NBN details from ticket comments"
                className="inline-flex items-center gap-1 text-xs text-amber-300 hover:text-amber-200"
              >
                <ExternalLink className="w-3 h-3" /> Find in Syncro
              </button>
            )}
            {!isPartnerView && client.syncro_customer_id && (
              <button
                data-testid="merge-to-syncro-btn"
                onClick={() => setShowSyncroMerge(true)}
                title="Relink this card to an existing Syncro customer and move the ticket (dedupe when auto-match created the wrong record)"
                className="inline-flex items-center gap-1 text-xs text-violet-300 hover:text-violet-200"
              >
                <Users className="w-3 h-3" /> Merge to Syncro customer
              </button>
            )}
          </div>
          <div className="flex gap-2 mb-1">
            <input
              data-testid="drawer-note-input"
              type="text" value={note} onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addNote()}
              placeholder="Add a note (e.g. Left VM — will retry Thursday)"
              className={inputCls + " flex-1"}
            />
            <button
              data-testid="drawer-note-add"
              onClick={addNote}
              className="px-3 py-2 rounded-lg border border-slate-800 hover:bg-slate-800 text-sm"
            >
              Log
            </button>
          </div>
          {!isPartnerView && (
            <div className="text-[11px] text-slate-500 mb-4 pl-1" data-testid="drawer-note-hint">
              Logging only records this internally. To email it to the partner,
              click <span className="font-semibold text-cyan-300">Email partner</span> on
              the new row.
            </div>
          )}
          <div className="space-y-2" data-testid="activity-feed">
            {activity === null ? (
              <div className="text-slate-500 text-sm">Loading…</div>
            ) : activity.length === 0 ? (
              <div className="text-slate-500 text-sm">No activity yet.</div>
            ) : activity.map(row => (
              <ActivityRow
                key={row.id}
                row={row}
                canEmailPartner={!isPartnerView}
                onEmailPartner={async () => {
                  if (!window.confirm(`Email this note to the partner (${board.name})?`)) return;
                  try {
                    const { data } = await api.post(
                      `/commissions/migrations/clients/${client.id}/activity/${row.id}/email-partner`);
                    if (data.already_emailed) {
                      toast.info("Already emailed earlier — no duplicate sent");
                    } else if (data.demo) {
                      toast.info(`Demo mode — would have emailed ${data.to}. Turn demo off in Business settings to send for real.`, { duration: 8000 });
                    } else {
                      toast.success(`Emailed to ${data.to}`);
                    }
                    loadActivity();
                  } catch (e) {
                    toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Email failed");
                  }
                }}
              />
            ))}
          </div>
        </div>
      </div>
      {showSyncroLinker && (
        <SyncroLinkerModal
          customer={client}
          onClose={() => setShowSyncroLinker(false)}
          onLinked={(fresh) => { onChanged(fresh); setShowSyncroLinker(false); }}
        />
      )}
      {showSyncroMerge && (
        <SyncroMergeModal
          client={client}
          onClose={() => setShowSyncroMerge(false)}
          onMerged={(fresh) => { onChanged(fresh); setShowSyncroMerge(false); loadActivity(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-panels (kept in-file — see file-header comment for rationale)
// ---------------------------------------------------------------------------

function MergeIntoPanel({ client, onMerged }) {
  const [candidates, setCandidates] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);

  async function load() {
    try {
      const { data } = await api.get(
        `/commissions/migrations/clients/${client.id}/merge-candidates`);
      setCandidates(data.candidates || []);
    } catch (_) { setCandidates([]); }
  }
  useEffect(() => { if (pickerOpen && candidates === null) load(); }, [pickerOpen, candidates]);

  async function doMerge(targetId) {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/commissions/migrations/clients/${client.id}/merge-into`,
        { target_client_id: targetId });
      onMerged(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Merge failed");
    } finally { setBusy(false); setConfirm(null); }
  }

  return (
    <div className="px-5 pb-5" data-testid="drawer-merge-into">
      <div className="rounded-lg border border-dashed border-violet-500/40 bg-violet-500/5 p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-violet-300 inline-flex items-center gap-2">
            <Upload className="w-3.5 h-3.5" /> Merge into imported card
          </h4>
          <button
            data-testid="merge-into-toggle"
            onClick={() => setPickerOpen(!pickerOpen)}
            className="text-xs text-violet-300 hover:text-violet-200"
          >
            {pickerOpen ? "Hide" : "Find matches"}
          </button>
        </div>
        <p className="text-xs text-slate-400 mb-3">
          This card was auto-created from a signup because no port-list match was found.
          If you recognise them as an existing imported customer, merge this record
          (signature + Syncro ticket) into the imported card — the wizard details are
          kept, this card is deleted.
        </p>
        {pickerOpen && (
          <div className="space-y-1.5" data-testid="merge-candidate-list">
            {candidates === null && <div className="text-xs text-slate-500">Loading…</div>}
            {candidates && candidates.length === 0 && (
              <div className="text-xs text-slate-500">
                No plausible matches found on the {client.partner_code} board.
                Manual link via the field above may still be the answer.
              </div>
            )}
            {candidates && candidates.map(c => (
              <button
                key={c.id}
                data-testid={`merge-candidate-${c.id}`}
                disabled={busy}
                onClick={() => setConfirm(c)}
                className="w-full text-left rounded-md border border-slate-800 hover:border-violet-500/60 hover:bg-slate-900 bg-slate-950/60 p-2.5 disabled:opacity-50 transition-colors"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm text-slate-100 font-medium truncate">
                    {c.business_name || c.contact_name || c.email || "(no name)"}
                  </div>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/20 border border-violet-500/50 text-violet-300 font-mono shrink-0">
                    {c.score}
                  </span>
                </div>
                <div className="text-[11px] text-slate-500 truncate">
                  {c.email || c.phone || c.service_address || "—"}
                </div>
                {(c.current_plan_name || c.external_customer_id) && (
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {c.current_plan_name && <span>Current: <span className="text-slate-300">{c.current_plan_name}</span></span>}
                    {c.external_customer_id && <span className="ml-2">Ext #{c.external_customer_id}</span>}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {confirm && (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setConfirm(null)}>
          <div className="bg-slate-900 border border-slate-800 rounded-xl max-w-lg w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="merge-confirm-modal">
            <h3 className="font-display text-lg tracking-tight mb-2">Merge cards?</h3>
            <p className="text-sm text-slate-400 mb-4">
              This will fold <span className="text-slate-100 font-medium">{client.business_name || client.email}</span> INTO{" "}
              <span className="text-violet-300 font-medium">{confirm.business_name || confirm.email}</span>. The signature,
              Syncro ticket #{client.syncro_ticket_number || "—"}, and chosen plan will move to the imported card.
              This card will be deleted. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirm(null)}
                className="px-3 py-2 text-sm rounded-lg border border-slate-800 hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                data-testid="merge-confirm-btn"
                disabled={busy}
                onClick={() => doMerge(confirm.id)}
                className="px-3 py-2 text-sm rounded-lg bg-violet-500 hover:bg-violet-400 text-white font-medium disabled:opacity-60"
              >
                {busy ? "Merging…" : "Merge"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ManualLinkPanel({ client, onLinked }) {
  const [signups, setSignups] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  async function load() {
    try {
      const { data } = await api.get(`/commissions/migrations/${client.partner_code}/unlinked-signups`);
      setSignups(data.signups || []);
    } catch (_) { setSignups([]); }
  }
  useEffect(() => { if (pickerOpen && signups === null) load(); }, [pickerOpen, signups]);

  async function link(signupId) {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/commissions/migrations/clients/${client.id}/link-signup`,
        { signup_id: signupId });
      toast.success("Signup linked to this customer");
      onLinked(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Link failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="px-5 pb-5" data-testid="drawer-manual-link">
      <div className="rounded-lg border border-dashed border-slate-800 bg-slate-950/40 p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 inline-flex items-center gap-2">
            <ExternalLink className="w-3.5 h-3.5" /> Manual link to signup
          </h4>
          <button
            data-testid="manual-link-toggle"
            onClick={() => setPickerOpen(!pickerOpen)}
            className="text-xs text-cyan-400 hover:text-cyan-300"
          >
            {pickerOpen ? "Hide" : "Pick a signup"}
          </button>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          If a customer completed the wizard but auto-match didn&apos;t link them (e.g. different email), pick their signup here.
        </p>
        {pickerOpen && (
          <div className="space-y-1.5" data-testid="manual-link-list">
            {signups === null && <div className="text-xs text-slate-500">Loading…</div>}
            {signups && signups.length === 0 && (
              <div className="text-xs text-slate-500">No unlinked signups for {client.partner_code}.</div>
            )}
            {signups && signups.map(s => (
              <button
                key={s.id}
                data-testid={`manual-link-option-${s.id}`}
                disabled={busy}
                onClick={() => link(s.id)}
                className="w-full text-left rounded-md border border-slate-800 hover:border-cyan-500/50 hover:bg-slate-900 bg-slate-950/60 p-2.5 disabled:opacity-50 transition-colors"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm text-slate-100 font-medium truncate">
                    {s.business_name || s.contact_name || s.email || "(no name)"}
                  </div>
                  <div className="text-[10px] text-slate-500 shrink-0">
                    {s.created_at ? fmtDate(s.created_at) : ""}
                  </div>
                </div>
                <div className="text-[11px] text-slate-500 truncate">
                  {s.email || s.phone || s.service_address || "—"}
                </div>
                {(s.plan?.name || s.syncro_ticket_number) && (
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {s.plan?.name && <span>Plan: <span className="text-slate-300">{s.plan.name}</span></span>}
                    {s.syncro_ticket_number && <span className="ml-2">Syncro #{s.syncro_ticket_number}</span>}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PortingDetails({ draft, setDraft }) {
  return (
    <div className="px-5 pb-5" data-testid="drawer-porting-details">
      <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-4">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3 inline-flex items-center gap-2">
          <FileText className="w-3.5 h-3.5" /> Porting details
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {PORT_FIELDS.map(f => (
            <label key={f.key} className="block">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1 block">{f.label}</span>
              <input
                data-testid={`drawer-port-${f.key}`}
                type={f.type || "text"}
                step={f.step}
                value={draft[f.key] || ""}
                onChange={(e) => setDraft({...draft, [f.key]: f.type === "number" ? parseFloat(e.target.value || 0) : e.target.value})}
                className={inputCls}
              />
              {f.hint && (
                <span className="text-[10px] text-slate-600 mt-1 block">{f.hint}</span>
              )}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

function NBNCredentialsPanel({ client, draft, setDraft, isPartnerView }) {
  const hasAny = client.nbn_username || client.nbn_password || client.nbn_speed || client.nbn_wsm_service_id || client.nbn_unid_port || client.avc_number;

  return (
    <div className="px-5 pb-4">
      <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 inline-flex items-center gap-2">
            <Wifi className="w-3.5 h-3.5 text-cyan-400" /> NBN handover credentials
          </h4>
          {!hasAny && (
            <span className="text-[10px] uppercase tracking-widest text-slate-600 italic">
              Awaiting provider handover
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <CredField label="Username" value={draft.nbn_username || ""}
            onChange={(v) => setDraft({ ...draft, nbn_username: v })}
            readOnly={isPartnerView} mono />
          <CredField label="Password" value={draft.nbn_password || ""}
            onChange={(v) => setDraft({ ...draft, nbn_password: v })}
            readOnly={isPartnerView} mono secret />
          <CredField label="Speed" value={draft.nbn_speed || ""}
            onChange={(v) => setDraft({ ...draft, nbn_speed: v })}
            readOnly={isPartnerView} placeholder="e.g. 100/40 Mbps" />
          <CredField label="WSM service ID" value={draft.nbn_wsm_service_id || ""}
            onChange={(v) => setDraft({ ...draft, nbn_wsm_service_id: v })}
            readOnly={isPartnerView} mono />
          <CredField label="UNI-D port" value={draft.nbn_unid_port || ""}
            onChange={(v) => setDraft({ ...draft, nbn_unid_port: v })}
            readOnly={isPartnerView} placeholder="e.g. 1" />
          <CredField label="AVC number" value={draft.avc_number || ""}
            onChange={(v) => setDraft({ ...draft, avc_number: v.toUpperCase() })}
            readOnly={isPartnerView} mono placeholder="AVC000123456789" />
        </div>
        {!isPartnerView && (
          <label className="mt-3 flex items-start gap-2 text-xs text-slate-400 cursor-pointer select-none">
            <input
              data-testid="drawer-pause-stage-automations"
              type="checkbox"
              checked={!!draft.stage_automations_paused}
              onChange={(e) => setDraft({ ...draft, stage_automations_paused: e.target.checked })}
              className="mt-0.5"
            />
            <span>
              <span className="text-slate-200 font-medium">Pause stage automation emails for this customer</span>
              <span className="block text-[11px] text-slate-500 leading-relaxed">
                When ticked, this customer won&apos;t receive automated stage-change emails or trigger the partner notification. Manual sends still work.
              </span>
            </span>
          </label>
        )}
        {hasAny && (
          <div className="mt-3 text-[11px] text-amber-300/90 inline-flex items-center gap-1.5">
            <AlertTriangle className="w-3 h-3" />
            Sensitive — password is encrypted at rest. Reveal only when sharing with the end customer.
          </div>
        )}
      </div>
    </div>
  );
}

function CredField({ label, value, onChange, readOnly, mono = false, secret = false, placeholder = "" }) {
  const [revealed, setRevealed] = useState(false);
  const showAsPassword = secret && !revealed;
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</label>
      <div className="mt-1 flex gap-1.5">
        <input
          type={showAsPassword ? "password" : "text"}
          value={value}
          disabled={readOnly}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`flex-1 bg-slate-950 border border-slate-800 focus:border-cyan-500 outline-none rounded-md px-3 py-1.5 text-sm disabled:cursor-default ${mono ? "font-mono" : ""}`}
          data-testid={`nbn-${label.toLowerCase().replace(/\s+/g,"-")}`}
        />
        {secret && value && (
          <button
            type="button"
            onClick={() => setRevealed((r) => !r)}
            className="px-2 rounded-md border border-slate-800 bg-slate-950 hover:bg-slate-800 text-slate-400 hover:text-slate-100"
            title={revealed ? "Hide password" : "Show password"}
            data-testid={`nbn-reveal-${label.toLowerCase().replace(/\s+/g,"-")}`}
          >
            {revealed ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
        )}
        {value && (
          <button
            type="button"
            onClick={async () => {
              try { await navigator.clipboard.writeText(value); toast.success(`${label} copied`); }
              catch { /* clipboard blocked — silent */ }
            }}
            className="px-2 rounded-md border border-slate-800 bg-slate-950 hover:bg-slate-800 text-slate-400 hover:text-slate-100"
            title={`Copy ${label}`}
            data-testid={`nbn-copy-${label.toLowerCase().replace(/\s+/g,"-")}`}
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Admin-only panel for attaching a signed form to a client who never
 * used the wizard link. Once uploaded, ``manual_signup`` on the client
 * doc satisfies the same consent gate as ``linked_signup_id`` — stage
 * progression past ``form_submitted`` unlocks and Syncro-push no
 * longer needs ``?force=true``. Overwrite-on-upload so replacing a
 * bad scan is one click, not two.
 */
function ManualSignupPanel({ client, onChanged }) {
  const [busy, setBusy] = useState(false);
  const attached = client.manual_signup || null;

  async function onFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";  // reset so re-selecting same file re-fires
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(
        `/commissions/migrations/clients/${client.id}/manual-signup`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success(attached ? "Signed form replaced" : "Signed form attached — signup gate satisfied");
      // Refresh the client so gates re-evaluate + the panel flips.
      const { data } = await api.get(`/commissions/migrations/clients/${client.id}`);
      onChanged(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  }

  async function remove() {
    if (!window.confirm("Remove the attached signed form? Stage progression will be blocked again until a new form is uploaded or the customer completes the wizard.")) return;
    setBusy(true);
    try {
      await api.delete(`/commissions/migrations/clients/${client.id}/manual-signup`);
      toast.success("Signed form removed");
      const { data } = await api.get(`/commissions/migrations/clients/${client.id}`);
      onChanged(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Remove failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="px-5 pb-5" data-testid="drawer-manual-signup">
      <div className={`rounded-lg border p-4 ${attached ? "border-emerald-500/40 bg-emerald-500/5" : "border-dashed border-slate-800 bg-slate-950/40"}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-xs uppercase tracking-widest font-semibold mb-1 inline-flex items-center gap-2 text-slate-300">
              <Upload className="w-3.5 h-3.5" /> Signed form (offline / paper)
            </div>
            {attached ? (
              <div className="text-sm text-slate-300">
                <a
                  href={`${api.defaults.baseURL}/commissions/migrations/clients/${client.id}/manual-signup`}
                  target="_blank" rel="noreferrer"
                  data-testid="drawer-manual-signup-download"
                  className="text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1 font-medium"
                >
                  <FileText className="w-3.5 h-3.5" />
                  {attached.filename || "signed-form"}
                  <ExternalLink className="w-3 h-3" />
                </a>
                <div className="text-[11px] text-slate-500 mt-1">
                  Uploaded {fmtDate(attached.uploaded_at)}
                  {attached.uploaded_by ? ` by ${attached.uploaded_by}` : ""}
                  {" · "}{Math.round((attached.size_bytes || 0) / 1024)} KB
                </div>
              </div>
            ) : (
              <div className="text-sm text-slate-400">
                Customer signed a paper or emailed PDF form? Upload it here and this card will pass the signup gate — same as if they'd used the wizard link. Accepted: PDF, PNG, JPG · max 8&nbsp;MB.
              </div>
            )}
          </div>
          <div className="shrink-0 flex flex-col items-end gap-1.5">
            <label
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md cursor-pointer transition-colors ${busy ? "opacity-60 cursor-wait" : ""} ${attached ? "bg-slate-800 hover:bg-slate-700 text-slate-100" : "bg-cyan-500 hover:bg-cyan-400 text-white"}`}
              data-testid="drawer-manual-signup-upload"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {attached ? "Replace" : "Upload signed form"}
              <input type="file" accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                     onChange={onFile} disabled={busy} className="hidden" />
            </label>
            {attached && (
              <button
                type="button" onClick={remove} disabled={busy}
                data-testid="drawer-manual-signup-remove"
                className="text-[11px] text-red-400 hover:text-red-300 disabled:opacity-50 inline-flex items-center gap-1"
              >
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ComparePanel({ client, isPartnerView, onChanged }) {
  const [matchedPlan, setMatchedPlan] = useState(null);
  const [signup, setSignup] = useState(null);
  const [loading, setLoading] = useState(false);
  const linkedSignupId = client.linked_signup_id;
  const matchedPlanId = client.matched_plan_id;
  const partnerCode = client.partner_code;

  useEffect(() => {
    if (!matchedPlanId && !linkedSignupId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [t, n] = await Promise.all([
          api.get(`/public/internet-plans`, { params: { partner_code: partnerCode || "", flow: "transfer" } }).then(r => r.data.plans || []).catch(() => []),
          api.get(`/public/internet-plans`, { params: { partner_code: partnerCode || "", flow: "new" } }).then(r => r.data.plans || []).catch(() => []),
        ]);
        const all = [...t, ...n];
        const plan = all.find(p => p.id === matchedPlanId);
        if (!cancelled) setMatchedPlan(plan || null);
        if (linkedSignupId && !isPartnerView) {
          try {
            const { data: s } = await api.get(`/commissions/internet-signups/${linkedSignupId}`);
            if (!cancelled) setSignup(s);
          } catch (_) { /* signup detail is best-effort */ }
        }
      } catch (_) { /* silent */ } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [matchedPlanId, linkedSignupId, partnerCode, isPartnerView]);

  const currentFee = Number(client.current_monthly_fee) || 0;
  const newFee = matchedPlan ? Number(matchedPlan.monthly_price) || 0 : 0;
  const saving = currentFee > 0 && newFee > 0 ? currentFee - newFee : null;
  const percent = saving != null && currentFee > 0 ? (saving / currentFee) * 100 : null;

  const mismatch = client.plan_mismatch || {};
  const showMismatchWarning = mismatch.is_mismatch && !mismatch.acknowledged;

  const [ackNote, setAckNote] = useState("");
  const [ackBusy, setAckBusy] = useState(false);
  async function acknowledgeMismatch() {
    setAckBusy(true);
    try {
      const { data } = await api.post(`/commissions/migrations/clients/${client.id}/plan-mismatch-ack`, { note: ackNote });
      toast.success("Plan mismatch acknowledged");
      setAckNote("");
      onChanged?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Ack failed");
    } finally { setAckBusy(false); }
  }
  async function clearAck() {
    setAckBusy(true);
    try {
      const { data } = await api.post(`/commissions/migrations/clients/${client.id}/plan-mismatch-clear`);
      toast.success("Acknowledgement cleared — mismatch will re-flag");
      onChanged?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Clear failed");
    } finally { setAckBusy(false); }
  }

  if (!linkedSignupId && !matchedPlanId) {
    return (
      <div className="px-5 pb-5" data-testid="drawer-compare-empty">
        <div className="rounded-lg border border-dashed border-slate-800 bg-slate-950/40 p-4 text-center">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">Awaiting signup</div>
          <div className="text-sm text-slate-400">
            This customer hasn&apos;t completed the transfer signup yet. When they do, we&apos;ll auto-link + show the side-by-side plan comparison here.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-5 pb-5" data-testid="drawer-compare">
      {showMismatchWarning && (
        <div className="mb-3 rounded-lg border-2 border-red-500/60 bg-red-500/10 p-4" data-testid="drawer-plan-mismatch-warning">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-300 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-widest font-bold text-red-200 mb-1">
                Plan mismatch — review before activation
              </div>
              <div className="text-sm text-red-100 mb-2">
                The plan this customer signed up to does NOT match what they&apos;re currently on.
                For transfer signups the goal is to keep them on the same speed / price — verify
                below and acknowledge before we activate + bill.
              </div>
              <ul className="text-xs text-red-200/90 space-y-1 mb-3 list-disc pl-5">
                {(mismatch.reasons || []).map((r) => <li key={r}>{r}</li>)}
              </ul>
              {!isPartnerView && (
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    data-testid="plan-mismatch-ack-note"
                    type="text" value={ackNote} onChange={(e) => setAckNote(e.target.value)}
                    placeholder="Reason (e.g. Customer opted to downgrade — confirmed by phone Jul 30)"
                    className="flex-1 bg-slate-950 border border-red-500/40 focus:border-red-400 outline-none rounded-md px-3 py-1.5 text-sm text-slate-100 placeholder:text-red-300/40"
                  />
                  <button
                    data-testid="plan-mismatch-ack-btn"
                    onClick={acknowledgeMismatch}
                    disabled={ackBusy || !ackNote.trim()}
                    title={!ackNote.trim() ? "Enter a reason first" : ""}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-red-500 hover:bg-red-400 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Acknowledge
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {mismatch.is_mismatch && mismatch.acknowledged && !isPartnerView && (
        <div className="mb-3 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 flex items-center justify-between gap-3" data-testid="drawer-plan-mismatch-acked">
          <div className="text-xs text-amber-200">
            <span className="uppercase tracking-widest font-bold">Mismatch acknowledged</span>
            <span className="text-amber-300/70">
              {" "}by {mismatch.ack?.by || "admin"} on {mismatch.ack?.at ? fmtDate(mismatch.ack.at) : ""}
            </span>
            {mismatch.ack?.note && (
              <div className="text-amber-100/80 mt-1 italic">&ldquo;{mismatch.ack.note}&rdquo;</div>
            )}
          </div>
          <button
            data-testid="plan-mismatch-clear-btn"
            onClick={clearAck} disabled={ackBusy}
            className="shrink-0 text-xs text-amber-300 hover:text-amber-100 underline"
          >
            Clear
          </button>
        </div>
      )}
      <div className={`rounded-lg border p-4 ${showMismatchWarning ? "border-red-500/40 bg-red-500/5" : "border-emerald-500/30 bg-emerald-500/5"}`}>
        <div className="flex items-center justify-between mb-3">
          <h4 className={`text-xs font-semibold uppercase tracking-wider inline-flex items-center gap-2 ${showMismatchWarning ? "text-red-300" : "text-emerald-300"}`}>
            <CheckCircle2 className="w-3.5 h-3.5" /> Signed & matched
          </h4>
          {signup?.pdf_url && (
            <a
              href={signup.pdf_url} target="_blank" rel="noreferrer"
              data-testid="drawer-signed-pdf-link"
              className="text-xs text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1"
            >
              <FileText className="w-3.5 h-3.5" /> Signed PDF <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
        {loading && <div className="text-xs text-slate-500">Loading plan…</div>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
              Current — {client.current_provider || "Provider"}
            </div>
            <div className="text-sm font-medium text-slate-100">{client.current_plan_name || "—"}</div>
            <div className="text-2xl font-display text-amber-300 mt-1">
              {currentFee > 0 ? fmtMoney(currentFee) : "—"}<span className="text-xs text-slate-500">/mo</span>
            </div>
            <div className="text-[10px] text-slate-500 mt-2 space-y-0.5">
              {client.service_start_date && <div>Since {fmtDate(client.service_start_date)}</div>}
              {client.current_ip && <div>IP: <span className="font-mono text-slate-400">{client.current_ip}</span></div>}
              {client.nbn_location_id && <div>LOC: <span className="font-mono text-slate-400">{client.nbn_location_id}</span></div>}
            </div>
          </div>
          <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-3">
            <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold mb-1">Steele Cloud (signed)</div>
            <div className="text-sm font-medium text-slate-100">{matchedPlan?.name || client.chosen_plan || "—"}</div>
            <div className="text-2xl font-display text-cyan-300 mt-1">
              {newFee > 0 ? fmtMoney(newFee) : "—"}<span className="text-xs text-slate-500">/mo</span>
            </div>
            <div className="text-[10px] text-slate-500 mt-2 space-y-0.5">
              {matchedPlan?.speed_download_mbps && (
                <div>{matchedPlan.speed_download_mbps}/{matchedPlan.speed_upload_mbps} Mbps</div>
              )}
              {signup?.syncro_ticket_number && (
                <div>Syncro ticket: <span className="font-mono text-slate-400">#{signup.syncro_ticket_number}</span></div>
              )}
              {signup?.created_at && (
                <div>Signed {fmtDate(signup.created_at)}</div>
              )}
            </div>
          </div>
        </div>
        {saving != null && (
          <div className="mt-3 rounded-md border border-slate-800 bg-slate-950/60 p-3 flex items-center justify-between">
            <div className="text-xs text-slate-400">Monthly difference</div>
            <div className={`font-display text-xl ${saving >= 0 ? "text-emerald-300" : "text-red-300"}`} data-testid="drawer-compare-saving">
              {saving >= 0 ? "−" : "+"}{fmtMoney(Math.abs(saving))}
              <span className="text-xs text-slate-500 ml-2">
                {percent != null ? `(${percent.toFixed(1)}%)` : ""}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ActivityRow({ row, canEmailPartner = false, onEmailPartner }) {
  const when = new Date(row.created_at).toLocaleString(undefined, {
    month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
  });
  const roleColor = row.actor_role === "admin" ? "text-cyan-300" :
                    row.actor_role === "partner" ? "text-emerald-300" : "text-slate-400";
  const EMAILABLE = new Set([
    "note_added", "syncro_pushed", "syncro_note_pushed", "syncro_comment_pulled",
  ]);
  const showEmailBtn = canEmailPartner && EMAILABLE.has(row.action);
  const emailedAt = row.emailed_to_partner_at;
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-3 text-sm">
      <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
        <span className={`${roleColor} font-medium`}>{row.actor_email || row.actor_role || "system"}</span>
        <span>·</span>
        <span>{when}</span>
        {showEmailBtn && (
          <div className="ml-auto inline-flex items-center gap-2">
            {emailedAt ? (
              <span
                className="inline-flex items-center gap-1 text-[10px] text-emerald-400"
                title={`Emailed to partner ${new Date(emailedAt).toLocaleString()}${row.emailed_to_partner_by ? ` by ${row.emailed_to_partner_by}` : ""}`}
              >
                <CheckCircle2 className="w-3 h-3" /> Emailed
              </span>
            ) : row.action === "note_added" && (
              <span
                className="inline-flex items-center gap-1 text-[10px] text-amber-300 font-semibold uppercase tracking-wider"
                title="This note is internal only. Click Email partner to send it to the partner."
              >
                Not emailed
              </span>
            )}
            <button
              type="button"
              onClick={onEmailPartner}
              data-testid={`activity-email-partner-${row.id}`}
              className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border transition-colors ${
                emailedAt
                  ? "border-slate-800 bg-slate-900 text-slate-500 hover:text-slate-300"
                  : "border-cyan-500/50 bg-cyan-500/20 text-cyan-200 hover:bg-cyan-500/30 pulse-cyan"
              }`}
              title={emailedAt ? "Already emailed — click to send again" : "Email this note to the partner"}
            >
              <Mail className="w-3 h-3" /> {emailedAt ? "Re-email" : "Email partner"}
            </button>
          </div>
        )}
      </div>
      <ActivitySummary row={row} />
    </div>
  );
}

function ActivitySummary({ row }) {
  const d = row.details || {};
  switch (row.action) {
    case "client_created":
      return <div className="text-slate-200">Created client <span className="text-slate-400">via {d.source || "manual"}</span></div>;
    case "client_deleted":
      return <div className="text-red-300">Deleted client &quot;{d.business_name}&quot;</div>;
    case "stage_changed":
      return <div className="text-slate-200"><span className="text-slate-500">Stage:</span> {d.from} → <span className="text-cyan-300 font-medium">{d.to}</span></div>;
    case "field_updated":
      return <div className="text-slate-200"><span className="text-slate-500">Set</span> {d.field} <span className="text-slate-500">to</span> &quot;{String(d.new)}&quot; <span className="text-slate-600">(was &quot;{String(d.old) || "empty"}&quot;)</span></div>;
    case "partner_email_reply":
      return (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold mb-1 inline-flex items-center gap-1.5">
            <Mail className="w-3 h-3" />
            Partner reply
            {d.ticket_number && <span className="text-slate-500 font-normal normal-case tracking-normal">· pushed to Syncro #{d.ticket_number} as private note</span>}
            {d.syncro_push_ok === false && <span className="text-amber-300 font-normal normal-case tracking-normal">· Syncro push failed</span>}
          </div>
          <div className="text-slate-200 whitespace-pre-wrap">{d.body || <span className="text-slate-500 italic">(empty reply)</span>}</div>
          {d.subject && <div className="text-[11px] text-slate-500 mt-1.5">Subject: {d.subject}</div>}
          {(d.auto_added_ccs || []).length > 0 && (
            <div className="text-[11px] text-cyan-400/70 mt-1">Auto-added CCs: {d.auto_added_ccs.join(", ")}</div>
          )}
        </div>
      );
    case "note_added":
      return (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-cyan-400/80 font-semibold mb-1">Kanban note</div>
          <div className="text-slate-200 whitespace-pre-wrap">{d.body}</div>
        </div>
      );
    case "syncro_comment_pulled": {
      const isDeleted = d.deleted_in_source === true;
      return (
        <div className={isDeleted ? "opacity-70" : ""}>
          <div className="text-[10px] uppercase tracking-widest text-violet-300 font-semibold mb-1 inline-flex items-center gap-1.5 flex-wrap">
            <ExternalLink className="w-3 h-3" />
            Syncro ticket
            {d.ticket_number && <span className="text-slate-500 font-normal normal-case tracking-normal">#{d.ticket_number}</span>}
            <span className="text-slate-600">·</span>
            <span className={d.hidden === false ? "text-emerald-300" : "text-amber-300"}>
              {d.hidden === false ? "customer-facing reply" : "hidden comment"}
            </span>
            {isDeleted && (
              <>
                <span className="text-slate-600">·</span>
                <span className="text-red-300 inline-flex items-center gap-1">
                  <Trash2 className="w-3 h-3" /> removed from Syncro
                </span>
              </>
            )}
          </div>
          <div className={`text-slate-200 whitespace-pre-wrap ${isDeleted ? "line-through decoration-red-400/60" : ""}`}>
            {d.body || <span className="text-slate-500 italic">(empty comment)</span>}
          </div>
          {d.author && <div className="text-[11px] text-slate-500 mt-1.5">— {d.author}</div>}
          {isDeleted && d.deleted_detected_at && (
            <div className="text-[10px] text-red-400/70 mt-1">
              Detected removed {new Date(d.deleted_detected_at).toLocaleString()}
            </div>
          )}
        </div>
      );
    }
    case "syncro_comment_deleted":
      return (
        <div className="text-red-300 text-xs inline-flex items-center gap-1.5">
          <Trash2 className="w-3 h-3" /> A Syncro comment was deleted (id {d.syncro_comment_id || "?"})
        </div>
      );
    case "syncro_pushed":
    case "syncro_note_pushed":
      return (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold mb-1 inline-flex items-center gap-1.5">
            <ArrowRight className="w-3 h-3" />
            Pushed to Syncro
            {d.ticket_number && <span className="text-slate-500 font-normal normal-case tracking-normal">#{d.ticket_number}</span>}
          </div>
          {d.body && <div className="text-slate-200 whitespace-pre-wrap">{d.body}</div>}
        </div>
      );
    case "static_ip_line_pushed":
      return (
        <div className="text-emerald-300 text-xs inline-flex items-center gap-1.5 flex-wrap">
          <CheckCircle2 className="w-3 h-3" />
          Static IP addon line added to recurring schedule
          {d.syncro_schedule_id && <span className="text-slate-500 font-mono">#{d.syncro_schedule_id}</span>}
        </div>
      );
    case "static_ip_line_skipped":
      return (
        <div className="text-amber-300 text-xs inline-flex items-center gap-1.5 flex-wrap">
          <AlertTriangle className="w-3 h-3" />
          Static IP addon push skipped — {String(d.reason || "").replace(/_/g, " ")}
        </div>
      );
    case "static_ip_line_failed":
      return (
        <div className="text-red-300 text-xs inline-flex items-center gap-1.5 flex-wrap">
          <AlertTriangle className="w-3 h-3" />
          Static IP addon push failed
          {d.response && <span className="text-slate-500 font-mono truncate max-w-xs">{d.response}</span>}
        </div>
      );
    case "plan_mismatch_acknowledged":
      return (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold mb-1">Plan mismatch acknowledged</div>
          {d.note && <div className="text-slate-200 italic">&ldquo;{d.note}&rdquo;</div>}
        </div>
      );
    case "plan_mismatch_ack_cleared":
      return <div className="text-red-300 text-xs">Plan mismatch acknowledgement cleared — re-flagged for review</div>;
    case "client_merged":
      return (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-violet-300 font-semibold mb-1">Merged in</div>
          <div className="text-slate-200">
            Folded &ldquo;<span className="text-slate-100 font-medium">{d.merged_from_business_name || d.merged_from_email || d.merged_from_id}</span>&rdquo; into this card
            {d.inherited_syncro_ticket_number && <span className="text-slate-500"> · brought Syncro #{d.inherited_syncro_ticket_number}</span>}
          </div>
        </div>
      );
    case "signup_linked":
      return <div className="text-slate-200"><span className="text-slate-500">Linked signup</span> {d.signup_id || ""}</div>;
    case "syncro_merged_to_customer":
      return (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-violet-300 font-semibold mb-1 inline-flex items-center gap-1.5">
            <ArrowRight className="w-3 h-3" />
            Merged into Syncro customer
            {d.to_syncro_customer_id && <span className="text-slate-500 font-normal normal-case tracking-normal font-mono">#{d.to_syncro_customer_id}</span>}
          </div>
          <div className="text-slate-200">
            {d.to_business_name && <span className="text-slate-100 font-medium">{d.to_business_name}</span>}
            {d.from_syncro_customer_id && (
              <span className="text-slate-500"> · from #{d.from_syncro_customer_id}</span>
            )}
            {d.ticket_moved && (
              <span className="text-emerald-300"> · ticket #{d.ticket_number || d.ticket_id} moved</span>
            )}
          </div>
        </div>
      );
    default:
      return (
        <div className="text-slate-300 text-sm">
          {row.action.replace(/_/g, " ")}
          {d.body && <div className="text-slate-200 whitespace-pre-wrap mt-1">{d.body}</div>}
        </div>
      );
  }
}
